import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import useEnablePricing from '@/enterprise/components/OfferBuilder/useEnablePricing';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { useEnterpriseSunspotUser } from '@/utils/useRestrictions';

export type SEAPricingSuggestionRequestBody =
  paths['/pricing-suggestions']['post']['requestBody']['content']['application/json'];

export type SEAPricingSuggestionResponse =
  paths['/pricing-suggestions']['post']['responses']['200']['content']['application/json'];

export type PricingData = SEAPricingSuggestionResponse['data'];

export type SEAPricingSuggestionSupplierMarket = SEAPricingSuggestionResponse['data']['supplierMarketGroups'][number];

export interface RateInfo {
  pricingRateUuid: string | undefined;
  trendingRate: number | undefined;
  trendingRateStart?: number;
  trendingRateEnd?: number;
  projectedClearingPercentage?: number;
}

export interface PreferredRateInfo {
  rate: number | undefined;
  rateType: string | undefined;
}

export interface CombinedPricingSuggestion {
  overallOptimalRateType: string | undefined;
  supplierMarkets: {
    marketUuid: string;
    supplierDivisionUuid: string;
    discount: {
      oneTime: RateInfo;
      recurring: RateInfo;
      expressAccept: RateInfo;
    };
    apr: {
      oneTime: RateInfo;
      recurring: RateInfo;
      expressAccept: RateInfo;
    };
    preferred?: {
      oneTime: PreferredRateInfo;
    };
  }[];
}

const extractPreferredRate = ({
  supplierMarketGroup,
  rateType,
}: {
  supplierMarketGroup: SEAPricingSuggestionSupplierMarket | undefined;
  rateType: string;
}): PreferredRateInfo => {
  const rateInformation = supplierMarketGroup?.rates.find((r) => r.rateInformationType === 'preferred');

  if (!rateInformation?.rates) {
    return { rate: undefined, rateType: undefined };
  }

  const foundRate = rateInformation.rates.find((r) => r.type === 'preferred' && r.rateType === rateType);

  if (!foundRate || !('rate' in foundRate)) {
    return { rate: undefined, rateType: undefined };
  }

  return { rate: foundRate.rate, rateType: foundRate.rateType };
};

export type PricingModifiers = SEAPricingSuggestionRequestBody['pricingModifiers'];

/**
 * only fetches pricing suggestions for price discovery
 * other pricing types are fetched in the useTakerMarkets hook
 */
export const fetchPricingSuggestion = async ({
  takerMarkets,
  isActionableOffer,
  includePreferred,
  isEnterpriseSunspotUser,
  pricingModifiers,
}: {
  takerMarkets: TakerMarket[];
  isActionableOffer: boolean;
  includePreferred?: boolean;
  isEnterpriseSunspotUser?: boolean;
  pricingModifiers?: PricingModifiers;
}) => {
  if (!takerMarkets.length) {
    return null;
  }

  // Sunspot users only receive PRICE_DISCOVERY rates from the managed pricing path.
  const rateInformationTypes: SEAPricingSuggestionRequestBody['rateInformationTypes'] = isEnterpriseSunspotUser
    ? ['price-discovery']
    : ['express-accept', 'price-discovery'];

  if (!isEnterpriseSunspotUser && includePreferred) {
    rateInformationTypes.push('preferred');
  }

  /**
   * fetches per taker market
   * { supplierMarketGroups: [{ supplierMarkets: [{ makerOrganizationUuid, marketUuid, supplierDivisionUuid, amount, dpeWeightedAverage }] }] }
   */
  const response = await apiClient
    .post(`api/supplier-experience/pricing-suggestions`, {
      json: {
        frequencies: ['one-time', 'recurring'],
        isActionableOffer,
        rateTypes: ['APR', 'DISC'],
        rateInformationTypes,
        supplierMarketGroups: takerMarkets.map((takerMarket) => ({
          supplierMarkets: [
            {
              makerOrganizationUuid: takerMarket.makerOrganizationUuid,
              marketUuid: takerMarket.marketUuid,
              supplierDivisionUuid: takerMarket.takerDivisionUuid,
              amount: takerMarket.eligibleInvoiceAmount,
              dpeWeightedAverage: takerMarket.eligibleDpeWeightedAvg,
            },
          ],
        })),
        ...(pricingModifiers && { pricingModifiers }),
      } satisfies SEAPricingSuggestionRequestBody,
    })
    .json<SEAPricingSuggestionResponse>();

  return response.data;
};

const extractRate = ({
  supplierMarketGroup,
  type,
  rateType,
  frequency,
}: {
  supplierMarketGroup: SEAPricingSuggestionSupplierMarket | undefined;
  type: string;
  rateType: string;
  frequency: string;
}): RateInfo => {
  const rateInformation = supplierMarketGroup?.rates.find((r) => r.rateInformationType === type);

  if (!rateInformation?.rates) {
    return { pricingRateUuid: undefined, trendingRate: undefined };
  }

  const foundRate = rateInformation.rates.find((rate) => {
    return (
      rate.type === rateInformation.rateInformationType &&
      rate.rateType === rateType &&
      'frequency' in rate &&
      rate.frequency === frequency
    );
  });

  if (!foundRate || !('trendingRate' in foundRate) || !Number.isFinite(foundRate.trendingRate)) {
    return { pricingRateUuid: undefined, trendingRate: undefined };
  }

  const rateInfo: RateInfo = {
    pricingRateUuid: foundRate.pricingRateUuid,
    trendingRate: foundRate.trendingRate,
  };

  // add range info and projected clearing percentage for price-discovery
  if (type === 'price-discovery') {
    if ('trendingRateStart' in foundRate && 'trendingRateEnd' in foundRate) {
      rateInfo.trendingRateStart = foundRate.trendingRateStart;
      rateInfo.trendingRateEnd = foundRate.trendingRateEnd;
    }

    if ('projectedClearingPercentage' in foundRate) {
      rateInfo.projectedClearingPercentage = foundRate.projectedClearingPercentage;
    }
  }

  return rateInfo;
};

const usePricingSuggestion = ({
  takerMarkets,
  isActionableOffer = false,
  alwaysFetch = false,
  includePreferred = false,
  pricingModifiers,
}: {
  takerMarkets: TakerMarket[];
  isActionableOffer?: boolean;
  /**
   * When true, bypasses the enablePricing account-level gate and always fetches pricing suggestions.
   * Use for flows that require pricing regardless of the supplier's pricing feature flag status,
   * without implying the context is an actionable offer.
   */
  alwaysFetch?: boolean;
  includePreferred?: boolean;
  pricingModifiers?: PricingModifiers;
}) => {
  // Even if pricing feature flag is disabled or pricing for account is disabled, we should still fetch pricing suggestions for EA AOs
  const enablePricing = useEnablePricing();
  const isEnterpriseSunspotUser = useEnterpriseSunspotUser();
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: ['pricing-suggestion', ...takerMarketUuidsMap, includePreferred, isEnterpriseSunspotUser],
    queryFn:
      enablePricing || isActionableOffer || alwaysFetch
        ? () =>
            fetchPricingSuggestion({
              takerMarkets,
              isActionableOffer,
              includePreferred,
              isEnterpriseSunspotUser,
              pricingModifiers,
            })
        : () => null,
    select: (data): CombinedPricingSuggestion => {
      return {
        overallOptimalRateType: data?.overallOptimalRateType,
        supplierMarkets: (data?.supplierMarketGroups ?? []).map((supplierMarketGroup) => ({
          marketUuid: supplierMarketGroup.supplierMarkets[0].marketUuid,
          supplierDivisionUuid: supplierMarketGroup.supplierMarkets[0].supplierDivisionUuid,
          apr: {
            oneTime: extractRate({
              frequency: 'one-time',
              rateType: 'APR',
              supplierMarketGroup,
              type: 'price-discovery',
            }),
            recurring: extractRate({
              frequency: 'recurring',
              rateType: 'APR',
              supplierMarketGroup,
              type: 'price-discovery',
            }),
            expressAccept: extractRate({
              frequency: 'one-time',
              rateType: 'APR',
              supplierMarketGroup,
              type: 'express-accept',
            }),
          },
          discount: {
            oneTime: extractRate({
              frequency: 'one-time',
              rateType: 'DISC',
              supplierMarketGroup,
              type: 'price-discovery',
            }),
            recurring: extractRate({
              frequency: 'recurring',
              rateType: 'DISC',
              supplierMarketGroup,
              type: 'price-discovery',
            }),
            expressAccept: extractRate({
              frequency: 'one-time',
              rateType: 'DISC',
              supplierMarketGroup,
              type: 'express-accept',
            }),
          },
          ...(includePreferred && {
            preferred: {
              oneTime: extractPreferredRate({
                rateType: 'APR',
                supplierMarketGroup,
              }),
            },
          }),
        })),
      };
    },
  });
};

export default usePricingSuggestion;
